/*
 * FALLBACK ONLY: reached solely from the legacy branch of firstPass, when no
 * aligner/WhisperX helper answers (phones). Kept working per project
 * constraint — do not extend.
 */

/**
 * Gap fill — transcript COMPLETENESS, as opposed to boundary accuracy.
 *
 * WHY THIS EXISTS
 * ---------------
 * Every other alignment stage in this app answers "are these labels sitting on
 * the right audio?". None of them can answer "is every word that was spoken on
 * the list at all?" — and that is the failure the user keeps reporting as
 * "drift".
 *
 * The mechanism is worth spelling out, because it is invisible in every metric
 * the pipeline already has. Suppose the speaker said seven words and the first
 * pass listed six. There is no timing error anywhere: the six labels are laid
 * onto six of the seven acoustic events, each boundary anchored to a real
 * spectral onset, `alignmentScore` reports a healthy span-on-speech and the run
 * log looks clean. But from the missing word onward, every clip in the Verify
 * tab plays the word AFTER the one it is labelled with. To the ear that is
 * indistinguishable from a clock that ran fast — and re-cutting boundaries, which
 * is what the previous three stages do, cannot fix it, because the boundaries
 * were never wrong.
 *
 * MEASURED, on the user's 44.8 s Jordanian-Arabic recording, on the task as it
 * sat in IndexedDB after a full first pass + QA + burst re-segmentation:
 *
 *   24.38 s of detected speech, 3.41 s of it with NO label over it (86.0% recall)
 *   6 unlabelled runs holding 180 ms or more of solid speech, 3.09 s in total:
 *     3.56–4.64 (1.07 s)   24.18–25.19 (1.00 s)   26.70–27.02 (0.32 s)
 *     8.03–8.34 (0.31 s)   37.83–38.04 (0.21 s)   38.79–38.97 (0.18 s)
 *   and bursts whose labels cannot physically account for their length:
 *     36.63–38.60  1.90 s of speech, two labels ("يحكي" "لي")
 *     24.18–25.69  1.50 s of speech, one label  ("حلوه")
 *      3.56–4.64   1.07 s of speech, NO labels at all
 *
 * That is roughly one word in eight missing. Nothing downstream can recover it,
 * because the information was never in the pipeline.
 *
 * WHAT THIS DOES
 * --------------
 * 1. FIND. Unlabelled speech (the VAD mask minus the union of segment spans,
 *    runs merged across sub-100 ms breaks, kept at 180 ms of solid speech) and
 *    UNDER-DENSE bursts (a burst whose labels, at the fastest plausible delivery
 *    the syllable model allows, still cannot fill the speech in it).
 * 2. ASK. One tiny Gemini call per region — the audio alone, no label list to
 *    place, "transcribe everything audible, and if there is nothing, say so".
 *    That last clause is load-bearing: a gap is often a breath the VAD mistook
 *    for speech, and a model asked "what word is this?" will always find one.
 * 3. PLACE. New words are folded in with whatever labels the region already had
 *    and the whole run is laid back onto the region's acoustic onsets by
 *    `placeSegmentsInBurst` — the same dynamic program stage 5 uses, imported,
 *    not reimplemented.
 * 4. GUARD. The region is scored before and after with `scoreBurst`; a region
 *    that gets worse is discarded whole, and so is one whose placement is
 *    infeasible or crammed.
 *
 * WHAT IT DELIBERATELY DOES NOT DO
 * --------------------------------
 * Delete a label. If the model does not mention a word the region already had,
 * that word stays exactly where it was. Dropping labels is burst
 * re-segmentation's job (it is given the list and can say "this was never
 * here"); doing it from a completeness pass would let one bad clip answer
 * silently destroy human-reviewable work. The costs are asymmetric: an inserted
 * word is a `pending` row a human will see and can reject in one tap, a deleted
 * word is gone.
 *
 * BEST EFFORT, ALWAYS
 * -------------------
 * Runs inside clip QA, after the segments and clips are committed, sharing QA's
 * wall-clock budget. Every failure path — a dead API, an unparseable answer, an
 * infeasible placement, the budget running out — leaves the existing segments
 * byte-for-byte as they were.
 */

import { newId, reviseSegments, type SegmentRevision } from '../../db';
import { round3 } from '../../lib/num';
import type { Segment } from '../../types';
import { extractMono, sliceToWav } from '../audioSlicer';
import type { ResegmentItem } from './burstResegment';
import type { ClipQaAnswer, GapFillItem } from '../gemini';
import {
  detectDips,
  expectedDurationRange,
  placeSegmentsInBurst,
  scoreBurst,
} from './onsetAlign';
import { detectOnsets, type LabelledSpan, type OnsetCandidate } from '../onsetDetect';
import {
  computeSpeechEnvelope,
  findSpeechBursts,
  type Burst,
  type SpeechEnvelope,
} from '../vad';

/* ------------------------------------------------------------------ */
/* Tunables                                                            */
/* ------------------------------------------------------------------ */

/**
 * Unlabelled runs closer together than this are one gap.
 *
 * Same reasoning as `GAP_CLOSE_SEC` in the VAD: the silence inside a stop
 * consonant is not a boundary, and a 40 ms sliver of coverage from a neighbour
 * that happens to reach across one should not split a word's gap in two.
 */
const GAP_MERGE_SEC = 0.1;

/**
 * Solid speech a gap must hold before it is worth an API call.
 *
 * Below ~180 ms there is no room for a word (the syllable model's floor for a
 * one-syllable word is 130 ms plus onset margin), so anything smaller is a
 * boundary rounding artefact — of the 11 unlabelled runs on the calibration
 * file, the 5 under this line total 0.32 s and are all edge slack around a
 * segment that is otherwise correct.
 */
export const MIN_GAP_SEC = 0.18;

/** A burst must hold at least this much speech to be judged for density. */
const MIN_DENSE_BURST_SEC = 0.6;

/**
 * A burst is under-dense when its labels, at their fastest plausible delivery,
 * account for less than this fraction of the speech in it.
 *
 * The comparison uses `expectedDurationRange().minSec`, the FLOOR of the
 * syllable model, so this is not "the speaker was slow" — it is "even rattled
 * off as fast as a human can produce them, these labels cannot fill this
 * burst". At 0.55 on the calibration file it fires on 12 of 29 bursts, which is
 * too many to act on; the {@link MIN_UNDER_DENSE_SLACK_SEC} floor below cuts
 * that to the 7 where the shortfall is big enough to be a whole missing word.
 */
const UNDER_DENSE_RATIO = 0.55;

/** ...and the shortfall must be at least this many seconds to be worth a call. */
const MIN_UNDER_DENSE_SLACK_SEC = 0.45;

/** Context added to each side of the clip we send, when a neighbour allows it. */
const CONTEXT_SEC = 0.15;

/**
 * Fraction of a neighbouring segment that is its CORE and may not be included
 * in a gap clip. Context helps the model hear a word's edges; half a neighbour
 * in the clip invites it to transcribe the neighbour and hand us a duplicate.
 */
const NEIGHBOUR_CORE_FRAC = 0.6;

/** Nothing this stage produces is ever shorter than this. */
const MIN_ITEM_SEC = 0.06;

/** Regions sent to Gemini per task, largest shortfall first. */
const MAX_GAP_REGIONS = 12;

/** More items than this in one answer means the model wrote an essay. Reject. */
const MAX_REGION_ITEMS = 24;

/** A region longer than this is not a gap, it is a sentence. Skip. */
const MAX_REGION_SEC = 6;

/** Gap clips are sent at 16 kHz mono, like every other QA request. */
const GAP_SAMPLE_RATE = 16000;

/**
 * Ceiling on the confidence an inserted label may carry.
 *
 * These words were missed by a pass that heard the whole recording in context
 * and then found by one that heard a fraction of a second in isolation. They are
 * guesses, they are `verify: 'pending'`, and they must sort alongside the other
 * things a human needs to look at — never above them.
 */
export const GAP_FILL_MAX_CONFIDENCE = 0.5;

/**
 * How much local score a region may lose and still keep its rebuild.
 *
 * Small but not zero, for the same reason `alignToOnsets` tolerates a little:
 * the score is blind to WHICH word is where, so a correctly split burst can
 * score a hair below a wrongly split one when the split leaves a few frames of
 * breath uncovered. Wide enough for that, far too narrow for a region that
 * actually broke.
 */
const GUARD_TOLERANCE = 0.02;

/* ------------------------------------------------------------------ */
/* 1 — measurement                                                     */
/* ------------------------------------------------------------------ */

/** One stretch of detected speech that no segment covers. */
export interface GapSpan {
  startSec: number;
  endSec: number;
  /** Seconds of speech inside it that are genuinely uncovered. */
  speechSec: number;
}

/** See {@link measureCompleteness}. */
export interface CompletenessReport {
  speechCoveredFrac: number;
  unlabelledSpeechSec: number;
  totalSpeechSec: number;
  /** Gaps holding at least {@link MIN_GAP_SEC} of solid speech. */
  gaps: GapSpan[];
  gapCount: number;
  gapSec: number;
  segmentCount: number;
}

/** Every unlabelled run of speech, merged and filtered. Ascending. */
export function findUnlabelledSpans(
  segments: readonly { startSec: number; endSec: number }[],
  env: SpeechEnvelope,
  minGapSec = MIN_GAP_SEC,
): GapSpan[] {
  const { mask, hopSec } = env;
  const frames = mask.length;
  if (frames === 0) return [];

  const covered = new Uint8Array(frames);
  for (const s of segments) {
    const from = Math.max(0, Math.floor(s.startSec / hopSec));
    const to = Math.min(frames, Math.ceil(s.endSec / hopSec));
    for (let i = from; i < to; i++) covered[i] = 1;
  }

  const raw: GapSpan[] = [];
  let i = 0;
  while (i < frames) {
    if (!mask[i] || covered[i]) {
      i++;
      continue;
    }
    let j = i;
    while (j < frames && mask[j] && !covered[j]) j++;
    raw.push({ startSec: i * hopSec, endSec: j * hopSec, speechSec: (j - i) * hopSec });
    i = j;
  }

  // Merge across short breaks, keeping `speechSec` a count of genuinely
  // uncovered speech rather than the merged span (the break between two runs is
  // either silence or already labelled, and must not be counted as either).
  const merged: GapSpan[] = [];
  for (const g of raw) {
    const last = merged[merged.length - 1];
    if (last && g.startSec - last.endSec < GAP_MERGE_SEC) {
      last.endSec = g.endSec;
      last.speechSec += g.speechSec;
    } else {
      merged.push({ ...g });
    }
  }

  return merged.filter((g) => g.speechSec >= minGapSec).map((g) => ({
    startSec: round3(g.startSec),
    endSec: round3(g.endSec),
    speechSec: round3(g.speechSec),
  }));
}

/**
 * How much of the audible speech carries a label at all.
 *
 * `speechCoveredFrac` is the same quantity `alignmentScore` calls `speechRecall`
 * and is recomputed here rather than imported so this module can be exercised
 * (and its before/after pair measured) without building a whole scoring context.
 * The number that is NOT available anywhere else is {@link CompletenessReport.gapCount}:
 * recall of 0.86 can mean 100 ms shaved off every segment or one whole sentence
 * missing, and only the second is the bug this stage exists for.
 */
export function measureCompleteness(
  segments: readonly { startSec: number; endSec: number }[],
  env: SpeechEnvelope,
): CompletenessReport {
  const { mask, hopSec, speechFrames } = env;
  const frames = mask.length;
  const covered = new Uint8Array(frames);
  for (const s of segments) {
    const from = Math.max(0, Math.floor(s.startSec / hopSec));
    const to = Math.min(frames, Math.ceil(s.endSec / hopSec));
    for (let i = from; i < to; i++) covered[i] = 1;
  }
  let hit = 0;
  for (let i = 0; i < frames; i++) if (mask[i] && covered[i]) hit++;

  const total = speechFrames.length;
  const gaps = findUnlabelledSpans(segments, env);
  return {
    speechCoveredFrac: total > 0 ? round3(hit / total) : 1,
    unlabelledSpeechSec: round3((total - hit) * hopSec),
    totalSpeechSec: round3(total * hopSec),
    gaps,
    gapCount: gaps.length,
    gapSec: round3(gaps.reduce((a, g) => a + g.speechSec, 0)),
    segmentCount: segments.length,
  };
}

/* ------------------------------------------------------------------ */
/* 2 — planning                                                        */
/* ------------------------------------------------------------------ */

export interface GapRegion {
  /** Why this region was picked. Logged; the handling is identical. */
  cause: 'gap' | 'under-dense';
  /** Audio window to cut and send, context included. */
  clipStartSec: number;
  clipEndSec: number;
  /** Bounds inside which segments may be created or moved. Never crossed. */
  placeLoSec: number;
  placeHiSec: number;
  /** Where the VAD measured speech inside the placement window. */
  speechStartSec: number;
  speechEndSec: number;
  /** Existing items overlapping the placement window; `[from, to)`. */
  from: number;
  to: number;
  /** Seconds of speech the current labels cannot account for. Ranking key. */
  unexplainedSec: number;
}

/** Seconds of speech inside `[a, b)` per the VAD mask. */
function speechBetween(env: SpeechEnvelope, a: number, b: number): number {
  const from = Math.max(0, Math.floor(a / env.hopSec));
  const to = Math.min(env.mask.length, Math.ceil(b / env.hopSec));
  let n = 0;
  for (let i = from; i < to; i++) if (env.mask[i]) n++;
  return n * env.hopSec;
}

/** Fastest plausible total delivery of these labels, per the syllable model. */
function fastestDelivery(segs: readonly LabelledSpan[]): number {
  let sum = 0;
  for (const s of segs) sum += expectedDurationRange(s.heard, s.kind).minSec;
  return sum;
}

/**
 * Which regions of this recording are missing labels?
 *
 * Two independent detectors, deliberately. A gap catches the case where nothing
 * at all is claiming a stretch of audio, which is the easy half; under-density
 * catches the case where a label IS claiming it and cannot possibly be that
 * long, which is the half that produces the perceptual drift, because there the
 * missing word is hidden inside a neighbour rather than left visibly bare.
 *
 * A gap that falls inside an under-dense burst is dropped: the burst region is a
 * superset, and sending both would spend two calls on one stretch of audio and
 * then race to insert into it.
 */
export function planGapRegions(
  items: readonly { startSec: number; endSec: number; heard: string; kind: string }[],
  env: SpeechEnvelope,
  bursts: readonly Burst[],
  durationSec: number,
  limit = MAX_GAP_REGIONS,
): GapRegion[] {
  const regions: GapRegion[] = [];

  /* -- under-dense bursts ------------------------------------------- */
  for (const burst of bursts) {
    const speechSec = speechBetween(env, burst.startSec, burst.endSec);
    if (speechSec < MIN_DENSE_BURST_SEC) continue;

    const inside = indexRangeIn(items, burst.startSec, burst.endSec);
    const group = items.slice(inside[0], inside[1]);
    const fastest = fastestDelivery(group);
    if (fastest >= UNDER_DENSE_RATIO * speechSec) continue;
    const slack = speechSec - fastest;
    if (slack < MIN_UNDER_DENSE_SLACK_SEC) continue;

    const region = buildRegion(
      items,
      env,
      durationSec,
      Math.min(burst.startSec, group[0]?.startSec ?? burst.startSec),
      Math.max(burst.endSec, group[group.length - 1]?.endSec ?? burst.endSec),
    );
    if (region) regions.push({ ...region, cause: 'under-dense', unexplainedSec: round3(slack) });
  }

  /* -- bare gaps ------------------------------------------------------ */
  for (const gap of findUnlabelledSpans(items, env)) {
    const mid = (gap.startSec + gap.endSec) / 2;
    if (regions.some((r) => mid >= r.placeLoSec && mid <= r.placeHiSec)) continue;
    const region = buildRegion(items, env, durationSec, gap.startSec, gap.endSec);
    if (region) regions.push({ ...region, cause: 'gap', unexplainedSec: gap.speechSec });
  }

  return regions
    .filter((r) => r.clipEndSec - r.clipStartSec > MIN_ITEM_SEC && r.clipEndSec - r.clipStartSec <= MAX_REGION_SEC)
    .sort((a, b) => b.unexplainedSec - a.unexplainedSec)
    .slice(0, limit)
    .sort((a, b) => a.placeLoSec - b.placeLoSec);
}

/**
 * Items overlapping `[a, b)` by more than a hair, as a contiguous `[from, to)`.
 *
 * When NOTHING overlaps — which is the normal case for a bare gap, and was the
 * bug that made the gap detector look like it found nothing at all — the empty
 * range must still be positioned correctly in the list, because `buildRegion`
 * reads `items[from - 1]` and `items[to]` to find out which neighbours it may
 * not disturb. Returning `[0, 0]` there put every gap's neighbours at the very
 * start of the recording and the region was then discarded as zero-length.
 */
function indexRangeIn(
  items: readonly { startSec: number; endSec: number }[],
  a: number,
  b: number,
): [number, number] {
  let from = -1;
  let to = -1;
  for (let i = 0; i < items.length; i++) {
    const overlap = Math.min(items[i].endSec, b) - Math.max(items[i].startSec, a);
    if (overlap > 0.02) {
      if (from < 0) from = i;
      to = i + 1;
    }
  }
  if (from >= 0) return [from, to];

  // Empty: the insertion point is the first item that ends after the span begins.
  let k = 0;
  while (k < items.length && items[k].endSec <= a + 1e-9) k++;
  return [k, k];
}

/**
 * Turn a target span into a region: what to cut, and where segments may go.
 *
 * The two windows are different on purpose. `place*` may never cross a segment
 * that is not part of this region, because that segment is somebody else's audio
 * and this stage has no mandate over it. `clip*` may reach a little further, up
 * to but not into the neighbour's core, because a word cut flush at its onset is
 * exactly the clip a model mis-hears — the context is there to be heard, not to
 * be transcribed.
 */
function buildRegion(
  items: readonly { startSec: number; endSec: number }[],
  env: SpeechEnvelope,
  durationSec: number,
  targetStart: number,
  targetEnd: number,
): Omit<GapRegion, 'cause' | 'unexplainedSec'> | null {
  const [from, to] = indexRangeIn(items, targetStart, targetEnd);

  const prev = from > 0 ? items[from - 1] : null;
  const next = to < items.length ? items[to] : null;

  const placeLo = Math.max(0, prev ? prev.endSec : 0, Math.min(targetStart, items[from]?.startSec ?? targetStart));
  const placeHi = Math.min(
    durationSec,
    next ? next.startSec : durationSec,
    Math.max(targetEnd, items[to - 1]?.endSec ?? targetEnd),
  );
  if (placeHi - placeLo <= MIN_ITEM_SEC) return null;

  const leftRoom = prev ? Math.max(0, (prev.endSec - prev.startSec) * (1 - NEIGHBOUR_CORE_FRAC) / 2) : CONTEXT_SEC;
  const rightRoom = next ? Math.max(0, (next.endSec - next.startSec) * (1 - NEIGHBOUR_CORE_FRAC) / 2) : CONTEXT_SEC;
  const clipStart = Math.max(0, placeLo - Math.min(CONTEXT_SEC, leftRoom));
  const clipEnd = Math.min(durationSec, placeHi + Math.min(CONTEXT_SEC, rightRoom));

  // The audible part of the PLACEMENT window, measured — this is what the model
  // is told about and what the placement DP is anchored to.
  const speech = speechEdges(env, placeLo, placeHi);
  if (!speech) return null;

  return {
    clipStartSec: round3(clipStart),
    clipEndSec: round3(clipEnd),
    placeLoSec: round3(placeLo),
    placeHiSec: round3(placeHi),
    speechStartSec: round3(speech[0]),
    speechEndSec: round3(speech[1]),
    from,
    to,
  };
}

/** First and last speech frame inside `[a, b)`, in seconds. Null if silent. */
function speechEdges(env: SpeechEnvelope, a: number, b: number): [number, number] | null {
  const from = Math.max(0, Math.floor(a / env.hopSec));
  const to = Math.min(env.mask.length, Math.ceil(b / env.hopSec));
  let first = -1;
  let last = -1;
  for (let i = from; i < to; i++) {
    if (!env.mask[i]) continue;
    if (first < 0) first = i;
    last = i;
  }
  if (first < 0) return null;
  return [Math.max(a, first * env.hopSec), Math.min(b, (last + 1) * env.hopSec)];
}

/* ------------------------------------------------------------------ */
/* 3 — folding an answer into the labels a region already has          */
/* ------------------------------------------------------------------ */

/** One entry of the merged label list a region is rebuilt from. */
interface MergedEntry {
  /** The existing item this is, or null when the model heard something new. */
  source: ResegmentItem | null;
  startSec: number;
  endSec: number;
  text: string;
  kind: Segment['kind'];
  confidence: number;
}

/**
 * Interleave what the model heard with what the region already had.
 *
 * A longest-common-subsequence alignment under the QA loop's own fuzzy label
 * matcher. Everything the model matched to an existing label keeps that label,
 * that row and its human-facing history; everything it did not match is new.
 * Existing labels the model failed to mention are NOT dropped — see the module
 * header — they are kept in place, in their original order.
 *
 * Order comes out of the traceback, so a new word heard between two known ones
 * lands between them, which is the entire point: that is where the missing word
 * that shifted everything downstream actually was.
 *
 * ONE EXISTING LABEL MAY ABSORB SEVERAL HEARD ROWS. Since burst-first grouping a
 * label can be several words joined with spaces, and the gap clip is transcribed
 * word by word — so "انا اسمي امجد" meets three rows. Matching it against only
 * the first would leave the other two looking new, and this stage would insert
 * duplicates of words it already had, which is precisely the mess the duplicate
 * resolver was written to clean up after. The alignment therefore lets an
 * existing entry consume a RUN of up to as many heard rows as its label has
 * words, longest run first, and the DP scores a run by its length so absorbing
 * more is preferred to inserting more.
 */
export function mergeHeardWithExisting(
  existing: readonly ResegmentItem[],
  heard: readonly { startSec: number; endSec: number; text: string; kind: Segment['kind']; confidence: number }[],
  matches: (a: string, b: string) => boolean,
): MergedEntry[] {
  const m = existing.length;
  const n = heard.length;

  const wordsIn = (text: string): number => (text ?? '').trim().split(/\s+/).filter(Boolean).length;

  /*
   * How many heard rows starting at `j` the existing label `i` accounts for.
   * 0 means "no match at all". Longest first, so a group is never matched to a
   * prefix of itself when the whole of it is there.
   */
  const runCache: Int32Array[] = [];
  for (let i = 0; i <= m; i++) runCache.push(new Int32Array(n + 1).fill(-1));
  const runLen = (i: number, j: number): number => {
    if (i >= m || j >= n) return 0;
    const cached = runCache[i][j];
    if (cached >= 0) return cached;
    const cap = Math.min(Math.max(1, wordsIn(existing[i].seg.heard)), n - j);
    let best = 0;
    for (let r = cap; r >= 1; r--) {
      const text = heard
        .slice(j, j + r)
        .map((h) => h.text)
        .join(' ')
        .trim();
      if (!text) continue;
      if (matches(existing[i].seg.heard, text)) {
        best = r;
        break;
      }
    }
    runCache[i][j] = best;
    return best;
  };

  // dp[i][j] = heard rows accounted for by the best alignment of existing[i..]
  // with heard[j..]. Identical to the old "matched pairs" count when every label
  // is one word, because then a run is always one row long.
  const dp: Int32Array[] = [];
  for (let i = 0; i <= m; i++) dp.push(new Int32Array(n + 1));
  for (let i = m - 1; i >= 0; i--) {
    for (let j = n - 1; j >= 0; j--) {
      const r = runLen(i, j);
      const withRun = r > 0 ? dp[i + 1][j + r] + r : -1;
      dp[i][j] = Math.max(withRun, dp[i + 1][j], dp[i][j + 1]);
    }
  }

  const out: MergedEntry[] = [];
  let i = 0;
  let j = 0;
  while (i < m && j < n) {
    const r = runLen(i, j);
    if (r > 0 && dp[i][j] === dp[i + 1][j + r] + r) {
      out.push(fromExisting(existing[i]));
      i++;
      j += r;
      continue;
    }
    /*
     * Neither side matched. The alignment length is the same whichever we emit
     * first, and TIME breaks the tie — without that, a region where the model
     * agreed with nothing (which is the interesting case: 20.83–22.62 s came
     * back as "بدي احاول اعملها" against labels "بطريقه تكون مرتبه") emitted all
     * three old labels and then all three new ones, so three words heard at the
     * FRONT of the burst were laid out across its back half. The whole value of
     * this stage is putting a missed word where it was actually said.
     */
    const takeExisting =
      dp[i + 1][j] > dp[i][j + 1]
        ? true
        : dp[i + 1][j] < dp[i][j + 1]
          ? false
          : existing[i].seg.startSec <= heard[j].startSec;
    if (takeExisting) {
      out.push(fromExisting(existing[i]));
      i++;
    } else {
      out.push(fromHeard(heard[j]));
      j++;
    }
  }
  while (i < m) out.push(fromExisting(existing[i++]));
  while (j < n) out.push(fromHeard(heard[j++]));

  // A blank-text "unclear" the model emitted with nothing to say is not a
  // segment anyone can label or verify; it would only ever become a mystery row.
  return out.filter((e) => e.source !== null || e.text.trim() !== '');
}

function fromExisting(item: ResegmentItem): MergedEntry {
  return {
    source: item,
    startSec: item.seg.startSec,
    endSec: item.seg.endSec,
    text: item.seg.heard,
    kind: item.seg.kind,
    confidence: item.seg.confidence,
  };
}

function fromHeard(row: {
  startSec: number;
  endSec: number;
  text: string;
  kind: Segment['kind'];
  confidence: number;
}): MergedEntry {
  return {
    source: null,
    startSec: row.startSec,
    endSec: row.endSec,
    text: row.text.trim(),
    kind: row.kind,
    confidence: Math.min(row.confidence, GAP_FILL_MAX_CONFIDENCE),
  };
}

/* ------------------------------------------------------------------ */
/* 4 — the stage                                                       */
/* ------------------------------------------------------------------ */

export interface GapFillStats {
  /** Unlabelled runs of >= 180 ms found before the stage ran. */
  gapsFound: number;
  /** Seconds of speech in them. */
  gapSecFound: number;
  /** Bursts whose labels cannot account for their length. */
  underDenseBursts: number;
  /** Regions planned (gaps + under-dense, deduplicated). */
  regionsPlanned: number;
  /** Regions actually sent to Gemini (cap and budget applied). */
  regionsAttempted: number;
  /** Regions where the model heard something new and it was accepted. */
  regionsFilled: number;
  /** Regions where the model heard nothing at all — the VAD was wrong. */
  regionsEmpty: number;
  /** Regions whose answer was rejected by the placement guard. */
  regionsRejected: number;
  /** New segments written. */
  segmentsInserted: number;
  /** Existing segments given new boundaries to make room. */
  segmentsRetimed: number;
  /** Inserted segments that then passed isolation QA. */
  insertedPassedQa: number;
  /** Regions left untouched because the shared QA budget ran out. */
  regionsSkippedByBudget: number;
  /** Gap-fill requests issued (retries excluded — the caller counts those). */
  apiCalls: number;
  /** Completeness before and after. The headline pair. */
  before?: CompletenessReport;
  after?: CompletenessReport;
  /** One line per region, for the run log. */
  regions: GapRegionLog[];
}

export interface GapRegionLog {
  cause: 'gap' | 'under-dense';
  startSec: number;
  endSec: number;
  unexplainedSec: number;
  existingLabels: string[];
  /** What the model said, or `null` when it was never asked / never answered. */
  heard?: string[];
  inserted: string[];
  outcome: 'filled' | 'nothing-heard' | 'nothing-new' | 'rejected' | 'no-answer' | 'skipped-budget' | 'skipped-cap';
  note?: string;
}

export interface GapFillDeps {
  buffer: AudioBuffer;
  /** Padding the stored clips are cut with, so re-QA hears the same audio. */
  padSec?: number;
  signal?: AbortSignal;
  /** Shared with clip QA: `Date.now()` past which no further call may be made. */
  deadlineAt?: number;
  /** Send one gap clip. Null on any failure the caller gave up on. */
  askGap: (
    startSec: number,
    endSec: number,
    knownLabels: string[],
    clip: Blob,
    speech: { startSec: number; endSec: number },
  ) => Promise<GapFillItem[] | null>;
  /** Re-listen to one window in isolation. Null = unknown, never a failure. */
  askClip: (startSec: number, endSec: number) => Promise<ClipQaAnswer | null>;
  /** The QA loop's own pass/fail judgement, so both stages agree. */
  passes: (seg: Pick<Segment, 'heard' | 'kind'>, answer: ClipQaAnswer) => boolean;
  /** The QA loop's fuzzy label comparison (`labelsMatch`), injected to avoid a cycle. */
  matches: (expected: string, actual: string) => boolean;
  onProgress?: (done: number, total: number) => void;
}

/**
 * Find the speech nothing is labelling and put labels on it, in place.
 *
 * `items` is mutated: inserted segments are spliced in and re-timed neighbours
 * rewritten, so the QA stages after this one see one flat, correctly ordered
 * list. Each region is persisted as it completes (segments, clips and the
 * renumbering they force, in one transaction), so an abort halfway through
 * still leaves a coherent task.
 */
export async function runGapFill(items: ResegmentItem[], deps: GapFillDeps): Promise<GapFillStats> {
  const { buffer, padSec, signal, deadlineAt, askGap, askClip, passes, matches, onProgress } = deps;
  const outOfTime = (): boolean => deadlineAt !== undefined && Date.now() > deadlineAt;

  const stats: GapFillStats = {
    gapsFound: 0,
    gapSecFound: 0,
    underDenseBursts: 0,
    regionsPlanned: 0,
    regionsAttempted: 0,
    regionsFilled: 0,
    regionsEmpty: 0,
    regionsRejected: 0,
    segmentsInserted: 0,
    segmentsRetimed: 0,
    insertedPassedQa: 0,
    regionsSkippedByBudget: 0,
    apiCalls: 0,
    regions: [],
  };
  if (items.length === 0) return stats;

  const duration = buffer.duration;
  const mono = extractMono(buffer, 0, duration);
  const env = computeSpeechEnvelope(mono, buffer.sampleRate);
  const bursts = findSpeechBursts(env);
  // One flux pass for the whole file; each region reads the slice it needs.
  const { onsets } = detectOnsets(mono, buffer.sampleRate);

  const snapshot = () => items.map((it) => it.seg);
  stats.before = measureCompleteness(snapshot(), env);
  stats.gapsFound = stats.before.gapCount;
  stats.gapSecFound = stats.before.gapSec;

  const regions = planGapRegions(snapshot(), env, bursts, duration);
  stats.underDenseBursts = regions.filter((r) => r.cause === 'under-dense').length;
  stats.regionsPlanned = regions.length;
  if (import.meta.env.DEV) {
    console.info(
      '[lableforge] gap-fill plan',
      regions.map(
        (r) => `${r.cause} ${r.placeLoSec.toFixed(2)}-${r.placeHiSec.toFixed(2)} (${r.unexplainedSec.toFixed(2)}s unexplained)`,
      ),
    );
  }
  if (regions.length === 0) {
    stats.after = stats.before;
    return stats;
  }

  const taskId = items[0].seg.taskId;
  let processed = 0;

  /** Write the current list back, contiguously numbered, in one transaction. */
  async function persist(): Promise<void> {
    const revisions: SegmentRevision[] = [];
    items.forEach((item, index) => {
      if (item.seg.index !== index) {
        item.seg.index = index;
        item.dirty = true;
      }
      if (!item.dirty) return;
      revisions.push(
        item.clipDirty
          ? { segment: item.seg, clip: sliceToWav(buffer, item.seg.startSec, item.seg.endSec, { padSec }) }
          : { segment: item.seg },
      );
      item.dirty = false;
      item.clipDirty = false;
    });
    await reviseSegments(revisions);
  }

  /*
   * Right to left, for the same reason burst re-segmentation walks that way: an
   * insertion changes how many items sit in the list, which would invalidate
   * every index planned after it. Going backwards means the indices still ahead
   * of us are exactly the ones we have not touched.
   */
  for (let r = regions.length - 1; r >= 0; r--) {
    if (signal?.aborted) break;
    const region = regions[r];
    const log: GapRegionLog = {
      cause: region.cause,
      startSec: region.placeLoSec,
      endSec: region.placeHiSec,
      unexplainedSec: region.unexplainedSec,
      existingLabels: items.slice(region.from, region.to).map((it) => it.seg.heard),
      inserted: [],
      outcome: 'skipped-cap',
    };
    const finish = () => {
      stats.regions.unshift(log);
      onProgress?.(++processed, regions.length);
    };

    if (outOfTime()) {
      stats.regionsSkippedByBudget++;
      log.outcome = 'skipped-budget';
      finish();
      continue;
    }

    /*
     * Re-clamp against the CURRENT neighbours rather than the planned ones. A
     * region processed a moment ago may have pushed its first segment left into
     * the silence this one planned to use; walking right to left means the
     * neighbour on our right is already final, so asking it again here is what
     * keeps two regions from colliding.
     */
    const originals = items.slice(region.from, region.to);
    const prevEnd = region.from > 0 ? items[region.from - 1].seg.endSec : 0;
    const nextStart = region.to < items.length ? items[region.to].seg.startSec : duration;
    const placeLo = Math.max(region.placeLoSec, prevEnd, 0);
    const placeHi = Math.min(region.placeHiSec, nextStart, duration);
    if (placeHi - placeLo <= MIN_ITEM_SEC) {
      log.note = 'no room left after the neighbours moved';
      log.outcome = 'rejected';
      finish();
      continue;
    }
    const speechStart = Math.max(placeLo, region.speechStartSec);
    const speechEnd = Math.min(placeHi, region.speechEndSec);
    if (speechEnd - speechStart <= MIN_ITEM_SEC) {
      log.note = 'no measured speech left in the window';
      log.outcome = 'rejected';
      finish();
      continue;
    }

    /* -- ask ---------------------------------------------------------- */
    const clipStart = Math.max(0, Math.min(region.clipStartSec, placeLo));
    const clipEnd = Math.min(duration, Math.max(region.clipEndSec, placeHi));
    const clip = sliceToWav(buffer, clipStart, clipEnd, {
      padSec: 0,
      minSec: 0,
      targetSampleRate: GAP_SAMPLE_RATE,
    });
    stats.regionsAttempted++;
    const answer = await askGap(
      clipStart,
      clipEnd,
      originals.map((o) => o.seg.heard),
      clip,
      { startSec: speechStart - clipStart, endSec: speechEnd - clipStart },
    );
    stats.apiCalls++;
    if (!answer) {
      log.outcome = 'no-answer';
      finish();
      continue;
    }
    log.heard = answer.map((a) => a.text);

    /*
     * An empty list is the answer we asked for and it is a real result: the VAD
     * called a breath speech, or the "gap" is the tail of a word a neighbour
     * already covers. Nothing is inserted and nothing is touched.
     */
    if (answer.length === 0) {
      stats.regionsEmpty++;
      log.outcome = 'nothing-heard';
      finish();
      continue;
    }
    if (answer.length > MAX_REGION_ITEMS) {
      stats.regionsRejected++;
      log.outcome = 'rejected';
      log.note = `${answer.length} items is not a gap`;
      finish();
      continue;
    }

    /* -- fold in, keeping only what lands inside the window ------------ */
    const heard = answer
      .map((a) => ({
        startSec: clipStart + Math.min(a.startSec, a.endSec),
        endSec: clipStart + Math.max(a.startSec, a.endSec),
        text: a.text,
        kind: a.kind,
        confidence: a.confidence,
      }))
      .filter((a) => a.text.trim() !== '')
      // Items whose centre sits outside the placement window are the context we
      // deliberately included in the clip being transcribed back at us.
      .filter((a) => {
        const mid = (a.startSec + a.endSec) / 2;
        return mid >= placeLo - 1e-9 && mid <= placeHi + 1e-9;
      })
      .sort((a, b) => a.startSec - b.startSec);

    const merged = mergeHeardWithExisting(originals, heard, matches);
    const newCount = merged.filter((e) => e.source === null).length;
    if (newCount === 0) {
      log.outcome = 'nothing-new';
      log.note = 'everything the model heard was already labelled';
      finish();
      continue;
    }

    /* -- place the merged list onto the region's onsets ---------------- */
    const spans: LabelledSpan[] = merged.map((e) => ({
      startSec: e.startSec,
      endSec: e.endSec,
      heard: e.text,
      kind: e.kind,
    }));
    const candidates: OnsetCandidate[] = [
      ...onsets.filter((o) => o.timeSec >= speechStart && o.timeSec < speechEnd),
      ...detectDips(env, speechStart, speechEnd),
    ].sort((a, b) => a.timeSec - b.timeSec);

    const placement = placeSegmentsInBurst(spans, speechStart, speechEnd, candidates);
    if (!placement || placement.crammed) {
      stats.regionsRejected++;
      log.outcome = 'rejected';
      log.note = placement ? 'window too short for the merged list' : 'no feasible placement';
      finish();
      continue;
    }

    const rebuilt = spans.map((s, k) => ({
      ...s,
      startSec: round3(Math.max(placeLo, placement.starts[k])),
      endSec: round3(Math.min(placeHi, placement.ends[k])),
    }));
    if (!isWellFormed(rebuilt, placeLo, placeHi)) {
      stats.regionsRejected++;
      log.outcome = 'rejected';
      log.note = 'placement broke an invariant';
      finish();
      continue;
    }

    /* -- guard --------------------------------------------------------- */
    const before = scoreBurst(originals.map((o) => o.seg), env, speechStart, speechEnd);
    const after = scoreBurst(rebuilt, env, speechStart, speechEnd);
    if (after < before - GUARD_TOLERANCE) {
      stats.regionsRejected++;
      log.outcome = 'rejected';
      log.note = `local score ${before.toFixed(3)} -> ${after.toFixed(3)}`;
      finish();
      continue;
    }

    /* -- build the new rows -------------------------------------------- */
    const rows: ResegmentItem[] = merged.map((e, k) => {
      const startSec = rebuilt[k].startSec;
      const endSec = rebuilt[k].endSec;
      if (e.source) {
        const moved =
          Math.abs(startSec - e.source.seg.startSec) > 1e-3 || Math.abs(endSec - e.source.seg.endSec) > 1e-3;
        if (moved) stats.segmentsRetimed++;
        return {
          seg: { ...e.source.seg, startSec, endSec, verify: 'pending' as const },
          // A re-timed segment must be re-listened to; an untouched one keeps
          // whatever verdict the recognition pass already gave it.
          passed: moved ? undefined : e.source.passed,
          dirty: moved,
          clipDirty: moved,
          removed: false,
        };
      }
      stats.segmentsInserted++;
      log.inserted.push(e.text);
      return {
        seg: {
          id: newId(),
          taskId,
          index: 0, // renumbered by persist()
          startSec,
          endSec,
          heard: e.text,
          contextual: e.text,
          confidence: Math.min(e.confidence, GAP_FILL_MAX_CONFIDENCE),
          kind: e.kind,
          verify: 'pending' as const,
        },
        dirty: true,
        clipDirty: true,
        removed: false,
      };
    });

    items.splice(region.from, region.to - region.from, ...rows);
    await persist();
    stats.regionsFilled++;
    log.outcome = 'filled';

    /* -- one round of isolation QA on what we just inserted ------------- */
    for (let k = 0; k < rows.length; k++) {
      if (signal?.aborted || outOfTime()) break;
      if (rows[k].passed !== undefined) continue;
      const check = await askClip(rows[k].seg.startSec, rows[k].seg.endSec);
      if (!check) continue;
      rows[k].passed = passes(rows[k].seg, check);
      if (rows[k].passed && merged[k].source === null) stats.insertedPassedQa++;
    }

    finish();
  }

  stats.after = measureCompleteness(snapshot(), env);
  return stats;
}

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

/** Ascending, non-overlapping, long enough, inside the window. */
function isWellFormed(segs: readonly LabelledSpan[], lo: number, hi: number): boolean {
  let cursor = lo - 1e-9;
  for (const s of segs) {
    if (s.startSec < cursor - 1e-9) return false;
    if (s.endSec - s.startSec < MIN_ITEM_SEC - 1e-9) return false;
    if (s.endSec > hi + 1e-9) return false;
    cursor = s.endSec;
  }
  return segs.length > 0;
}

export default runGapFill;
